/* eslint-disable react-hooks/exhaustive-deps */
import {
  Autocomplete,
  TextField,
  Typography,
  autocompleteClasses,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { ElementType, useEffect, useState } from 'react';
import { Noop } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { BuildingAddressValue } from 'src/configs/APISchemas/Application.schema';
import {
  GET_ORGANISATION_BUILDING_BY_NAME,
  GetOrganisationResponse,
} from 'src/configs/APISchemas/BuildingByOrganisationName';
import { ROUTES } from 'src/constants/routes';
import { GraphQLRequest } from 'src/helpers/GraphQLRequest';
import { useLocationSearch } from 'src/helpers/hooks/useLocationSearch';
import { updatePurchasePolicyInput } from '../../redux/actions/purchasePolicy';
import { updateCompanyVendor } from '../../redux/actions/shared';
import { getCompanyVendor } from '../../redux/selectors/shared';
import { getCompleteAddress } from './CompanyDropdown.helper';
import MuiListbox from './MuiListbox';
import Popper from './Popper';
import { ContactSupportId } from 'src/constants/constants';
import { logToBackend } from 'src/helpers/BackendLogger';

const PopperStyled: ElementType = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    border: '1px solid #1D1D1D',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

type Option = {
  value?: string;
  label?: string;
  key?: string;
};

type CompanyDropdownProps = {
  value: string | undefined;
  placeholder: string;
  testId?: string;
  onChange: (selectedAddress: BuildingAddressValue) => void;
  setPageLoading: React.Dispatch<React.SetStateAction<boolean>>;
  onBlur: Noop;
  handleCantFindAddressInCompanyList: () => void;
  handleCantFindAddressInCompanyListURL?: string;
};

const DONT_SEE_BUILDING_KEY = 'dontSeeYourBuildingButton';

const defaultOption: Option = {
  value: undefined,
  label: undefined,
};

const defaultOptions = [{ value: '', label: 'No Option found', address: '' }];

const getOptions = (listOfCompanyAddresses?: BuildingAddressValue[]) => {
  const options = listOfCompanyAddresses?.map((address) => ({
    value: address.id,
    label: address.name,
    address: address.complete_address,
    city: address.municipality,
  }));

  return options?.length ? options : defaultOptions;
};

const CompanyDropdown = ({
  value,
  placeholder,
  onChange,
  setPageLoading,
  onBlur,
  testId,
  handleCantFindAddressInCompanyList,
  handleCantFindAddressInCompanyListURL,
}: CompanyDropdownProps) => {
  const companyVendor = useSelector(getCompanyVendor);
  const dispatch = useDispatch();
  const history = useNavigate();
  const getUrlWithQueryParams = useLocationSearch();
  const [selectedOption, setSelectedOption] = useState<Option>(defaultOption);
  const [listOfCompanyAddresses, setListOfCompanyAddresses] = useState<
    BuildingAddressValue[]
  >([]);

  useEffect(() => {
    getListOfCompanyBuildingsAddresses();
  }, []);

  const dontSeeYourBuildingButton = {
    key: 'dontSeeYourBuildingButton',
    value: 'dontSeeYourBuildingButton',
    label: '',
    address: '',
    city: '',
    button: (
      <Typography variant="body1">
        Don’t see your building? <u>Click to add your address.</u>
      </Typography>
    ),
  };

  const handleDontSeeYourBuildingBtnClick = (selectedOption: Option) => {
    if (selectedOption?.key === DONT_SEE_BUILDING_KEY) {
      handleCantFindAddressInCompanyList();

      if (!handleCantFindAddressInCompanyListURL) {
        return;
      }

      history(getUrlWithQueryParams(handleCantFindAddressInCompanyListURL));
      window.location.reload(); //Just to make the user think that something loaded, otherwise it may be confusing
    }
  };

  const getListOfCompanyBuildingsAddresses = async () => {
    setPageLoading(true);
    const COMPANY_VENDOR = companyVendor || 'organisation';
    try {
      const query = GET_ORGANISATION_BUILDING_BY_NAME(COMPANY_VENDOR);
      const response = await GraphQLRequest<GetOrganisationResponse>(query);

      if (response?.buildingByOrganisationName?.buildings) {
        const buildings: BuildingAddressValue[] =
          response.buildingByOrganisationName.buildings.map((building) => {
            const { fullAddress } = building;
            const addressNumber = fullAddress.addressNumber
              ? fullAddress.addressNumber.toString()
              : '';

            return {
              id: building.id,
              name: building.name,
              state: fullAddress.region || '',
              street: fullAddress.street || '',
              postalCode: fullAddress.postalCode || '',
              addressNumber,
              municipality: fullAddress.municipality || '',
              complete_address: getCompleteAddress(fullAddress),
            };
          });

        setListOfCompanyAddresses(buildings);

        const selectedAddress = buildings.find(
          (building: any) => building.id === value,
        );

        setSelectedOption({
          value: selectedAddress?.id || '',
          label: selectedAddress?.name || '',
        });

        setPageLoading(false);
      } else {
        toast('Organisation not Found', { type: 'error' });
        handleCantFindCompanyList();
        history(ROUTES.COMPLIANCE_WELCOME_PATH);
        return;
      }
    } catch (error: any) {
      setPageLoading(false);
      if (error?.response?.data?.errors) {
        return toast(error.response.data.errors[0].message, {
          type: 'error',
        });
      }
      toast(`We are having some trouble, Please contact support for help. id: ${ContactSupportId.companyDropdown}`, {
        type: 'error',
      });
      setSelectedOption({
        value: '',
        label: '',
      });
      await logToBackend(error.toString())
    }
  };

  const handleAddressSelect = (_: any, selectedOption: Option) => {
    handleDontSeeYourBuildingBtnClick(selectedOption);

    if (!selectedOption || !selectedOption.label || !selectedOption.value) {
      return;
    }

    const value = selectedOption.value;
    const selectedAddress = listOfCompanyAddresses.find(
      (add: any) => add.id === value,
    );

    selectedAddress && onChange(selectedAddress);
  };

  const handleCantFindCompanyList = () => {
    dispatch(
      updatePurchasePolicyInput({
        buildingName: '',
        buildingId: '',
        addressName: '',
        address: {
          addressNumber: '',
          municipality: '',
          postalCode: '',
          state: '',
          street: '',
          shortNameStreet: '',
        },
      }),
    );
    dispatch(updateCompanyVendor(null));
  };

  return (
    <>
      {selectedOption.label != undefined && (
        <Autocomplete
          defaultValue={selectedOption}
          disableClearable={true}
          onBlur={onBlur}
          noOptionsText="No Option Found"
          disableListWrap
          PopperComponent={PopperStyled}
          ListboxComponent={MuiListbox}
          options={getOptions(listOfCompanyAddresses)}
          filterOptions={(options: any, { inputValue }) => {
            return [
              ...options.filter(
                (option: any) =>
                  option.label
                    .toLowerCase()
                    .includes(inputValue.toLowerCase()) ||
                  option.address
                    .toLowerCase()
                    .includes(inputValue.toLowerCase()),
              ),
              dontSeeYourBuildingButton,
            ];
          }}
          getOptionLabel={(option) => option.label || ''}
          onChange={handleAddressSelect}
          renderInput={(params) => (
            <TextField
              {...params}
              inputProps={{
                ...(params.inputProps || {}),
                'data-testid': testId,
              }}
              label={placeholder}
              required
            />
          )}
          renderOption={(props, option, state) =>
            [props, option, state.index] as React.ReactNode
          }
          // TODO: Post React 18 update - validate this conversion, look like a hidden bug
          renderGroup={(params) => params as unknown as React.ReactNode}
          sx={
            value
              ? {}
              : {
                  'label:before': {
                    content: '"Find "',
                  },
                  '&.Mui-focused': {
                    'label:before': {
                      content: '""',
                    },
                  },
                }
          }
        />
      )}
    </>
  );
};

export default CompanyDropdown;
